import { openDB, type DBSchema, type IDBPDatabase } from "idb";
import type { ExpenseBody, Op } from "@psplit/ledger-core";

export interface GroupMeta {
  groupId: string;
  name: string;
  keyB64: string; // CURRENT 32-byte group key, base64url - leaves this device only in invites
  epoch?: number; // which key that is; undefined or 0 = the founding key
  // Superseded keys, kept so entries written before a rotation stay readable.
  // Rotation shuts the door going forward; it does not un-read the past.
  pastKeys?: Array<{ epoch: number; keyB64: string }>;
  relayUrl: string; // "" = QR-sync only
  joinedAt: number;
  autoSync?: boolean; // sync right after local changes; undefined = on
}

export interface SyncState {
  groupId: string;
  cursor: number; // last relay seq ingested, for the topic of `epoch`
  epoch?: number; // epoch the cursor belongs to; a rotation moves the mailbox
  lastPushHeads: string[]; // frontier the relay is known to have from us
  lastSyncAt: number;
  lastHelloAt?: number; // last join announcement, throttled to one per 10 min
}

// A recurring template is DEVICE-LOCAL, not a ledger op: it materializes a
// normal signed expense op each month, always via an explicit user tap.
export interface RecurringTemplate {
  id: string;
  groupId: string;
  body: ExpenseBody;
  day: number; // 1..31, clamped to the month's length
  lastApplied: string; // "YYYY-MM" of the last month recorded (or skipped)
}

interface Schema extends DBSchema {
  kv: { key: string; value: unknown };
  groups: { key: string; value: GroupMeta };
  ops: {
    key: [string, string];
    value: { groupId: string; hash: string; op: Op };
    indexes: { byGroup: string };
  };
  syncstate: { key: string; value: SyncState };
  recurring: { key: string; value: RecurringTemplate; indexes: { byGroup: string } };
}

let dbPromise: Promise<IDBPDatabase<Schema>> | null = null;

export function db(): Promise<IDBPDatabase<Schema>> {
  dbPromise ??= openDB<Schema>("psplit", 3, {
    upgrade(d, oldVersion) {
      if (oldVersion < 1) {
        d.createObjectStore("kv");
        d.createObjectStore("groups", { keyPath: "groupId" });
        const ops = d.createObjectStore("ops", { keyPath: ["groupId", "hash"] });
        ops.createIndex("byGroup", "groupId");
        d.createObjectStore("syncstate", { keyPath: "groupId" });
      }
      if (oldVersion < 2) {
        const recurring = d.createObjectStore("recurring", { keyPath: "id" });
        recurring.createIndex("byGroup", "groupId");
      }
      // v3 adds key epochs. Rows written before this upgrade are epoch 0 by
      // omission, which is exactly what `epoch ?? 0` reads them as, so there is
      // no row rewriting to do here.
    },
  });
  return dbPromise;
}
